// Data structure › Tags (spec/dashboard.md section 5.6): the branch's tags with their document counts; a tag opens the
// search page filtered by it; Rename and Remove are staged — every document carrying the tag gets one `tags` edit, so
// Save All commits them as one batch and Discard All undoes them.
import { useState } from 'react';
import { Link } from 'react-router';
import { ops } from '../../api/operations';
import type { Document, Tag } from '../../api/types';
import { baseOf } from '../../content/actions';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { DataGrid } from '../../ui/DataGrid';
import { Field } from '../../ui/Field';
import { AlertCircle, Pencil, Tags, Trash2 } from '../../ui/icons';
import { TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { entityKey } from '../../workspace/Workspace';
import { SchemaTabs, useSchemaEnv } from './common';
import './schema.css';

export function TagsPage() {
  const env = useSchemaEnv();
  const { client, path, catalog, workspace, toasts, confirm, canWrite, fail, org, gameSlug, branch } = env;
  const [renaming, setRenaming] = useState<Tag | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  /** stages one `tags` edit per document carrying `tag`; `to` null removes it */
  const stage = async (tag: string, to: string | null): Promise<number> => {
    const docs = await client.all<Document>((cursor) => client.data(ops.listDocuments, { path, query: { tag, limit: 200, cursor } }), 100_000);
    let count = 0;
    for (const doc of docs) {
      const draft = workspace.getDraft(entityKey('document', doc.id))?.current as unknown as Document | undefined;
      const current = draft?.tags ?? doc.tags;
      if (!current.includes(tag)) continue;
      const next: string[] = [];
      for (const t of current) {
        const v = t === tag ? to : t;
        if (v && !next.includes(v)) next.push(v);
      }
      workspace.edit('document', doc.id, 'tags', next, { base: baseOf(doc), label: catalog.label(doc), template: doc.template });
      count++;
    }
    return count;
  };

  const rename = async () => {
    if (!renaming) return;
    const to = newName.trim();
    if (!to || to === renaming.name) return;
    setBusy(renaming.name);
    try {
      const n = await stage(renaming.name, to);
      toasts.success(`Rename of ${renaming.name} → ${to} staged on ${n} document${n === 1 ? '' : 's'}; Save All commits it.`);
      setRenaming(null);
    } catch (e) {
      fail(e, `Loading the documents tagged ${renaming.name} failed`);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (tag: Tag) => {
    const ok = await confirm({ title: `Remove the tag ${tag.name}?`, body: `The tag is removed from ${Number(tag.documents)} document${Number(tag.documents) === 1 ? '' : 's'}. The edits are staged; Save All commits them and Discard All undoes them.`, confirmLabel: 'Stage removal', tone: 'danger' });
    if (!ok) return;
    setBusy(tag.name);
    try {
      const n = await stage(tag.name, null);
      toasts.success(`Removal of ${tag.name} staged on ${n} document${n === 1 ? '' : 's'}; Save All commits it.`);
    } catch (e) {
      fail(e, `Loading the documents tagged ${tag.name} failed`);
    } finally {
      setBusy(null);
    }
  };

  const renameNote = renaming && newName.trim() && catalog.tags.some((t) => t.name === newName.trim() && t.name !== renaming.name) ? `${newName.trim()} exists: the documents get the existing tag (the two merge).` : null;

  return (
    <section className="schema-page">
      <div className="page-head">
        <h1>Data structure</h1>
        {!catalog.loading && <span className="page-meta">{catalog.tags.length} tag{catalog.tags.length === 1 ? '' : 's'}</span>}
      </div>
      <SchemaTabs />
      <DataGrid<Tag>
        id="tags"
        rows={catalog.tags}
        rowKey={(t) => t.name}
        loading={catalog.loading}
        caption={`${catalog.tags.length} tag${catalog.tags.length === 1 ? '' : 's'}`}
        emptyState={
          catalog.error ? (
            <EmptyState icon={<AlertCircle />} title="The tags did not load" body={catalog.error} />
          ) : (
            <EmptyState icon={<Tags />} title="No tags yet" body="A tag exists by being used: the tag input of every document editor creates one." />
          )
        }
        columns={[
          { key: 'name', header: 'Tag', fixed: true, render: (t) => <Link to={routes.search(org, gameSlug, branch, null, { tag: t.name })}>{t.name}</Link>, cellText: (t) => t.name, sortValue: (t) => t.name },
          { key: 'documents', header: 'Documents', align: 'right', render: (t) => Number(t.documents), sortValue: (t) => Number(t.documents) },
          {
            key: 'actions',
            header: '',
            width: 180,
            render: (t) => (
              <span className="row row-actions">
                <Button size="s" variant="ghost" icon={<Pencil aria-hidden="true" />} disabled={!canWrite || busy !== null} busy={busy === t.name} onClick={() => { setRenaming(t); setNewName(t.name); }}>
                  Rename
                </Button>
                <Button size="s" variant="ghost" icon={<Trash2 aria-hidden="true" />} disabled={!canWrite || busy !== null} onClick={() => void remove(t)}>
                  Remove
                </Button>
              </span>
            ),
          },
        ]}
      />
      {renaming && (
        <Modal
          title={`Rename tag ${renaming.name}`}
          open
          onClose={() => setRenaming(null)}
          footer={
            <>
              <Button onClick={() => setRenaming(null)}>Cancel</Button>
              <Button variant="primary" busy={busy !== null} disabled={!newName.trim() || newName.trim() === renaming.name} onClick={() => void rename()}>
                Stage rename
              </Button>
            </>
          }
        >
          <Field label="New name" help={`${Number(renaming.documents)} document${Number(renaming.documents) === 1 ? '' : 's'} get one staged edit each.${renameNote ? ` ${renameNote}` : ''}`}>
            <TextInput value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void rename()} />
          </Field>
        </Modal>
      )}
    </section>
  );
}
